import { useEffect, useMemo, useRef, useState } from "react";
import { Upload, ArrowRight, Check, AlertCircle, X, Loader2, PlusCircle } from "lucide-react";
import Papa from "papaparse";
import { api } from "../api/client";
import { DUNS, REQUIRED_FIELDS, HEADER_SUGGESTIONS } from "../types";
import type { CsvImportPayload, RecipientField } from "../types";

type Step = "upload" | "mapping" | "uploading" | "result";
type ColumnAssignment = {
  columnKey: string;
  targetType: "core" | "custom" | "skip";
  targetFieldKey: string;
};

type ColumnPreview = {
  columnKey: string;
  header: string;
  samples: string[];
};

export default function UploadPage() {
  const [step, setStep] = useState<Step>("upload");
  const [fileName, setFileName] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [columns, setColumns] = useState<ColumnPreview[]>([]);
  const [processedRows, setProcessedRows] = useState<Array<{ rowNum: number; values: Record<string, string> }>>([]);
  const [mapping, setMapping] = useState<ColumnAssignment[]>([]);
  const [dun, setDun] = useState("");
  const [useFirstRowAsHeaders, setUseFirstRowAsHeaders] = useState(true);
  const [detectDuplicateContacts, setDetectDuplicateContacts] = useState(true);
  const [coreFields, setCoreFields] = useState<RecipientField[]>([]);
  const [customFields, setCustomFields] = useState<RecipientField[]>([]);
  const [newFieldName, setNewFieldName] = useState("");
  const [pendingFieldColumn, setPendingFieldColumn] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [result, setResult] = useState<{
    success: boolean;
    uploadLogId?: number;
    undone?: boolean;
    totalRows: number;
    imported: number;
    updated: number;
    rejected: number;
    skippedBlank: number;
    duplicateInFile: number;
    duplicateExisting: number;
    errors: string[];
  } | null>(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadFields = async () => {
      try {
        const res = await api.getRecipientFields();
        setCoreFields(res.coreFields);
        setCustomFields(res.customFields);
      } catch {
        alert("Gagal mendapatkan senarai field import");
      }
    };
    loadFields();
  }, []);

  const rebuildCsvData = (rows: string[][], withHeader: boolean, existingMapping?: ColumnAssignment[]) => {
    if (!rows.length) {
      setColumns([]);
      setProcessedRows([]);
      setMapping([]);
      setTotalRows(0);
      return;
    }

    const maxColumns = rows.reduce((acc, row) => Math.max(acc, row.length), 0);
    const headerRow = withHeader ? rows[0] || [] : [];
    const dataRows = withHeader ? rows.slice(1) : rows;

    const nextColumns: ColumnPreview[] = Array.from({ length: maxColumns }, (_, index) => {
      const columnKey = `col_${index}`;
      const suggestedHeader = (headerRow[index] || "").trim();
      const header = suggestedHeader || `Column ${index + 1}`;
      const samples = dataRows
        .map((row) => (row[index] || "").trim())
        .filter((value) => value !== "")
        .slice(0, 4);

      return { columnKey, header, samples };
    });

    const nextRows = dataRows.map((row, index) => {
      const values: Record<string, string> = {};
      for (let columnIndex = 0; columnIndex < maxColumns; columnIndex += 1) {
        values[`col_${columnIndex}`] = (row[columnIndex] || "").trim();
      }
      return {
        rowNum: withHeader ? index + 2 : index + 1,
        values,
      };
    });

    const previous = new Map((existingMapping || mapping).map((item) => [item.columnKey, item]));
    const suggestedMapping = nextColumns.map((column) => {
      const existing = previous.get(column.columnKey);
      if (existing) return existing;

      const loweredHeader = column.header.toLowerCase();
      for (const [fieldKey, suggestions] of Object.entries(HEADER_SUGGESTIONS)) {
        if (suggestions.some((value) => loweredHeader.includes(value.toLowerCase()))) {
          return {
            columnKey: column.columnKey,
            targetType: "core" as const,
            targetFieldKey: fieldKey,
          };
        }
      }

      return {
        columnKey: column.columnKey,
        targetType: "skip" as const,
        targetFieldKey: "",
      };
    });

    setColumns(nextColumns);
    setProcessedRows(nextRows);
    setMapping(suggestedMapping);
    setTotalRows(nextRows.length);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith(".csv")) {
      alert("Sila muat naik fail CSV");
      return;
    }

    setFileName(file.name);

    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed = (results.data || []) as unknown[];
        const rows = parsed
          .map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? "")) : []))
          .filter((row) => row.length > 0);

        setRawRows(rows);
        rebuildCsvData(rows, useFirstRowAsHeaders, []);
        setStep("mapping");
      },
      error: () => {
        alert("Gagal membaca fail CSV");
      },
    });
  };

  const updateMapping = (columnKey: string, value: string) => {
    if (value === "add-field") {
      setPendingFieldColumn(columnKey);
      return;
    }

    let targetType: "core" | "custom" | "skip" = "skip";
    let targetFieldKey = "";

    if (value.startsWith("core:")) {
      targetType = "core";
      targetFieldKey = value.slice(5);
    } else if (value.startsWith("custom:")) {
      targetType = "custom";
      targetFieldKey = value.slice(7);
    }

    setMapping((prev) =>
      prev.map((m) => (m.columnKey === columnKey ? { ...m, targetType, targetFieldKey } : m))
    );
  };

  const validateMapping = () => {
    const mapped = mapping.filter((item) => item.targetType !== "skip");
    const requiredMissing = REQUIRED_FIELDS.filter(
      (fieldKey) => !mapped.some((item) => item.targetType === "core" && item.targetFieldKey === fieldKey)
    );
    const hasDunMapping = mapped.some(
      (item) => item.targetType === "core" && item.targetFieldKey === "dun"
    );

    const seen = new Map<string, number>();
    for (const item of mapped) {
      const key = `${item.targetType}:${item.targetFieldKey}`;
      seen.set(key, (seen.get(key) || 0) + 1);
    }
    const duplicates = Array.from(seen.entries()).filter(([, count]) => count > 1);

    return {
      requiredMissing,
      needsDunFallback: !hasDunMapping && !dun,
      hasDuplicate: duplicates.length > 0,
      isValid: requiredMissing.length === 0 && duplicates.length === 0 && (hasDunMapping || !!dun),
    };
  };

  const validation = useMemo(() => validateMapping(), [mapping]);

  const addCustomField = async () => {
    const name = newFieldName.trim();
    if (!name) {
      alert("Nama field diperlukan");
      return;
    }

    try {
      const res = await api.createRecipientField(name);
      const newField = res.field;
      if (!newField.key) return;

      setCustomFields((prev) => {
        const exists = prev.some((item) => item.key === newField.key);
        return exists ? prev : [...prev, newField];
      });

      if (pendingFieldColumn) {
        setMapping((prev) =>
          prev.map((item) =>
            item.columnKey === pendingFieldColumn
              ? { ...item, targetType: "custom", targetFieldKey: newField.key }
              : item
          )
        );
      }

      setPendingFieldColumn(null);
      setNewFieldName("");
    } catch {
      alert("Gagal menambah field custom");
    }
  };

  const BATCH_SIZE = 500;

  const handleUpload = async () => {
    if (validation.needsDunFallback) {
      alert("Sila pilih DUN fallback atau map kolum DUN dari CSV");
      return;
    }
    if (!validation.isValid) {
      alert("Sila lengkapkan mapping terlebih dahulu");
      return;
    }

    setIsUploading(true);
    setStep("uploading");

    // Split rows into batches of BATCH_SIZE
    const batches: Array<typeof processedRows> = [];
    for (let i = 0; i < processedRows.length; i += BATCH_SIZE) {
      batches.push(processedRows.slice(i, i + BATCH_SIZE));
    }
    const totalBatches = batches.length;
    setBatchProgress({ current: 0, total: totalBatches });

    const aggregated = {
      totalRows: 0,
      importedRows: 0,
      updatedRows: 0,
      rejectedRows: 0,
      skippedBlankRows: 0,
      duplicateInFileRows: 0,
      duplicateExistingRows: 0,
      errors: [] as string[],
      uploadLogId: undefined as number | undefined,
    };

    try {
      for (let i = 0; i < batches.length; i++) {
        setBatchProgress({ current: i + 1, total: totalBatches });
        const payload: CsvImportPayload = {
          fileName,
          dun,
          rows: batches[i],
          mappings: mapping,
          options: {
            useFirstRowAsHeaders,
            detectDuplicateContacts,
          },
        };
        const res = await api.uploadMappedRecipients(payload);
        aggregated.totalRows += res.totalRows ?? batches[i].length;
        aggregated.importedRows += res.importedRows ?? 0;
        aggregated.updatedRows += res.updatedRows ?? 0;
        aggregated.rejectedRows += res.rejectedRows ?? 0;
        aggregated.skippedBlankRows += res.skippedBlankRows ?? 0;
        aggregated.duplicateInFileRows += res.duplicateInFileRows ?? 0;
        aggregated.duplicateExistingRows += res.duplicateExistingRows ?? 0;
        if (res.errors?.length) aggregated.errors.push(...res.errors);
        // Undo only available for single-batch imports
        if (totalBatches === 1) aggregated.uploadLogId = res.uploadLogId;
      }

      setResult({
        success: true,
        uploadLogId: aggregated.uploadLogId,
        undone: false,
        totalRows: aggregated.totalRows,
        imported: aggregated.importedRows,
        updated: aggregated.updatedRows,
        rejected: aggregated.rejectedRows,
        skippedBlank: aggregated.skippedBlankRows,
        duplicateInFile: aggregated.duplicateInFileRows,
        duplicateExisting: aggregated.duplicateExistingRows,
        errors: aggregated.errors,
      });
      setStep("result");
    } catch {
      alert("Ralat berlaku semasa import");
      setStep("mapping");
    } finally {
      setIsUploading(false);
      setBatchProgress(null);
    }
  };

  const handleReset = () => {
    setStep("upload");
    setFileName("");
    setTotalRows(0);
    setRawRows([]);
    setColumns([]);
    setProcessedRows([]);
    setMapping([]);
    setDun("");
    setUseFirstRowAsHeaders(true);
    setDetectDuplicateContacts(true);
    setNewFieldName("");
    setPendingFieldColumn(null);
    setResult(null);
    setIsUndoing(false);
    setBatchProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUndoImport = async () => {
    if (!result?.uploadLogId || result.undone) return;

    const confirmed = window.confirm("Undo import ini? Data import akan dibatalkan.");
    if (!confirmed) return;

    setIsUndoing(true);
    try {
      const res = await api.undoUploadImport(result.uploadLogId);
      setResult((prev) => (prev ? { ...prev, undone: true } : prev));
      alert(
        `${res.message}\nRekod dipadam: ${res.undoneInserted}\nRekod dipulih: ${res.undoneUpdated}\nLangkau: ${res.skippedMissing}`
      );
    } catch {
      alert("Gagal undo import. Sila semak status data semasa.");
    } finally {
      setIsUndoing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Muat Naik Penerima</h1>
        <p className="text-slate-600">Muat naik fail CSV untuk import penerima</p>
      </div>

      {/* Steps Indicator */}
      <div className="flex items-center gap-4">
        <StepIndicator number={1} label="Upload" active={step === "upload"} completed={step !== "upload" && step !== "uploading"} />
        <ArrowRight className="w-5 h-5 text-slate-300" />
        <StepIndicator number={2} label="Peta Field" active={step === "mapping"} completed={step === "uploading" || step === "result"} />
        <ArrowRight className="w-5 h-5 text-slate-300" />
        <StepIndicator number={3} label="Siap" active={step === "result" || step === "uploading"} completed={false} />
      </div>

      {/* Upload Step */}
      {step === "upload" && (
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
          >
            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-700">Klik untuk upload fail CSV</p>
            <p className="text-sm text-slate-500 mt-2">Fail akan dibuka ke skrin pemetaan sebelum import</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
          />

          <div className="mt-6 bg-blue-50 rounded-lg p-4">
            <h3 className="font-medium text-blue-800 mb-2">Format CSV Yang Diterima:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Jika fail anda ada kolum DUN, anda tidak perlu pilih DUN fallback</li>
              <li>• Anda akan petakan setiap kolum di langkah seterusnya</li>
              <li>• Field wajib: Bil, Nama, No. IC, Institusi</li>
            </ul>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              DUN / Kawasan Fallback
            </label>
            <select
              value={dun}
              onChange={(e) => setDun(e.target.value)}
              className="w-full md:w-64 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">-- Guna DUN dari CSV jika ada --</option>
              {DUNS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-2">
              Digunakan hanya jika fail CSV tidak mempunyai kolum DUN atau ada baris yang kosong.
            </p>
          </div>
        </div>
      )}

      {/* Mapping Step */}
      {step === "mapping" && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Peta Kolum CSV</h2>
              <p className="text-sm text-slate-600">Semak setiap kolum, lihat sampel, kemudian tetapkan field sasaran.</p>
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={useFirstRowAsHeaders}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUseFirstRowAsHeaders(checked);
                    rebuildCsvData(rawRows, checked);
                  }}
                />
                Guna baris pertama sebagai header
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={detectDuplicateContacts}
                  onChange={(e) => setDetectDuplicateContacts(e.target.checked)}
                />
                Kesan duplikasi No. IC sedia ada
              </label>
            </div>
          </div>

          {(validation.requiredMissing.length > 0 || validation.hasDuplicate) && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 space-y-1">
              {validation.requiredMissing.length > 0 && (
                <p>Field wajib belum dipetakan: {validation.requiredMissing.join(", ")}</p>
              )}
              {validation.needsDunFallback && (
                <p>Pilih DUN fallback atau map kolum DUN dari CSV.</p>
              )}
              {validation.hasDuplicate && (
                <p>Terdapat field yang dipetakan lebih dari satu kali. Sila semak semula.</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {columns.map((column) => {
              const assignment = mapping.find((item) => item.columnKey === column.columnKey);
              const selectedValue = !assignment || assignment.targetType === "skip"
                ? "skip"
                : `${assignment.targetType}:${assignment.targetFieldKey}`;

              return (
                <div key={column.columnKey} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Kolum</p>
                  <p className="font-semibold text-slate-800 truncate">{column.header}</p>

                  <div className="mt-3">
                    <p className="text-xs text-slate-500 mb-1">Contoh nilai</p>
                    {column.samples.length > 0 ? (
                      <ul className="text-sm text-slate-700 space-y-1">
                        {column.samples.map((value, idx) => (
                          <li key={idx} className="truncate">• {value}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-slate-400">Tiada sampel</p>
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs text-slate-600 mb-1">Assign Column to</label>
                    <select
                      value={selectedValue}
                      onChange={(e) => updateMapping(column.columnKey, e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                    >
                      <option value="skip">Skip Column</option>
                      {coreFields.map((field) => (
                        <option key={`core:${field.key}`} value={`core:${field.key}`}>
                          {field.name}{field.required ? " *" : ""}
                        </option>
                      ))}
                      {customFields.length > 0 && (
                        <optgroup label="Custom Fields">
                          {customFields.map((field) => (
                            <option key={`custom:${field.key}`} value={`custom:${field.key}`}>
                              {field.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <option value="add-field">Add Field...</option>
                    </select>
                  </div>
                </div>
              );
            })}
          </div>

          {pendingFieldColumn && (
            <div className="mt-4 border border-blue-200 bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-3">Tambah field custom baru dan terus guna untuk kolum ini.</p>
              <div className="flex flex-col md:flex-row gap-2">
                <input
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  placeholder="Contoh: Intitusi Tambahan"
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm"
                />
                <button
                  onClick={addCustomField}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <PlusCircle className="w-4 h-4" />
                  Add Field
                </button>
                <button
                  onClick={() => {
                    setPendingFieldColumn(null);
                    setNewFieldName("");
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-white"
                >
                  Batal
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6 border-t border-slate-200 pt-4">
            <p className="text-sm text-slate-600">
              {totalRows} baris data sedia untuk diimport. DUN fallback: <span className="font-semibold">{dun || "Dari CSV"}</span>
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                Batal
              </button>
              <button
                onClick={handleUpload}
                disabled={!validation.isValid || isUploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Import Sekarang
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Uploading Step */}
      {step === "uploading" && (
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Sedang Memproses...</h2>
            {batchProgress && batchProgress.total > 1 ? (
              <>
                <p className="text-slate-600 mb-3">
                  Batch {batchProgress.current} / {batchProgress.total}
                </p>
                <div className="w-full max-w-sm mx-auto bg-slate-200 rounded-full h-2.5 mb-3">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.round((batchProgress.current / batchProgress.total) * 100)}%` }}
                  />
                </div>
                <p className="text-sm text-slate-500">
                  {totalRows} baris dibahagikan kepada {batchProgress.total} batch ({BATCH_SIZE} baris/batch)
                </p>
              </>
            ) : (
              <p className="text-slate-600 mb-4">
                {totalRows} baris sedang diproses untuk import.
              </p>
            )}
            <p className="text-sm text-slate-500 mt-3">
              Sila tunggu, ini mungkin mengambil masa beberapa minit untuk fail besar.
            </p>
          </div>
        </div>
      )}

      {/* Result Step */}
      {step === "result" && result && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          {result.success ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800">Muat Naik Berjaya!</h2>
                {result.undone && (
                  <p className="text-sm text-amber-700 mt-2">Import ini telah di-undo.</p>
                )}
              </div>

              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{result.totalRows}</p>
                  <p className="text-sm text-blue-700">Jumlah Baris</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{result.imported}</p>
                  <p className="text-sm text-green-700">Baru Import</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">{result.updated}</p>
                  <p className="text-sm text-amber-700">Dikemaskini</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{result.rejected}</p>
                  <p className="text-sm text-red-700">Gagal</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 text-sm">
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-slate-500">Baris kosong dilangkau</p>
                  <p className="font-semibold text-slate-800">{result.skippedBlank}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-slate-500">Duplikat dalam fail</p>
                  <p className="font-semibold text-slate-800">{result.duplicateInFile}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-slate-500">Duplikat sedia ada</p>
                  <p className="font-semibold text-slate-800">{result.duplicateExisting}</p>
                </div>
              </div>

              {result.errors.length > 0 && (
                <div className="bg-red-50 rounded-lg p-4 mb-6">
                  <h3 className="font-medium text-red-800 mb-2">Ralat:</h3>
                  <ul className="text-sm text-red-700 space-y-1">
                    {result.errors.map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">Muat Naik Gagal</h2>
              <p className="text-slate-600 mt-2">Sila cuba lagi</p>
            </div>
          )}

          <div className="flex justify-center gap-4 mt-6">
            {result.success && result.uploadLogId && (
              <button
                onClick={handleUndoImport}
                disabled={isUndoing || !!result.undone}
                className="px-6 py-2 border border-amber-400 text-amber-700 rounded-lg hover:bg-amber-50 disabled:opacity-50"
              >
                {isUndoing ? "Sedang Undo..." : result.undone ? "Sudah Undo" : "Undo Import"}
              </button>
            )}
            <button
              onClick={handleReset}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Muat Naik Lagi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepIndicator({ number, label, active, completed }: { number: number; label: string; active: boolean; completed: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          completed
            ? "bg-green-600 text-white"
            : active
            ? "bg-blue-600 text-white"
            : "bg-slate-200 text-slate-500"
        }`}
      >
        {completed ? <Check className="w-4 h-4" /> : number}
      </div>
      <span className={`text-sm ${active ? "text-slate-800 font-medium" : "text-slate-500"}`}>
        {label}
      </span>
    </div>
  );
}
